import axios, { AxiosError, CancelTokenSource } from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import map_filter_atom from '../../atom/map-filter'
import map_side_bar_list_atom from '../../atom/map-side-bar-list'
import { EstateModel } from '../../selector/get-map-data-selector'
import API from '../../service/api'
import ApiBaseResponseModel from '../../service/model/api-base-response-model'
import { apiCallback, getEstateDetailPath } from '../../shared/function'
import LoadingFallback from '../loading/loading-fallback'
import MapSideNodata from './map-side-nodata'
import MapSideLevelOver from './map-side-level-over'

  // [2025-06-05] by 새날_방용환
  // - 기존: map_ref를 useRef로 새로 생성
  // - 변경: 부모 컴포넌트에서 useRef 받아오기
  // - 목적: 다른 컴포넌트에서 map 객체 동일하게 사용하기 위해
interface Props {
  map_ref: React.MutableRefObject<any>
  fold: boolean
}

interface DataModel extends ApiBaseResponseModel {
  list: Array<EstateModel>
  is_last: boolean
}

/* eslint-disable @typescript-eslint/no-unused-vars */
const MapSideBarList = (props: Props) => {
  const { map_ref, fold } = props

  // [2025-06-10] by 새날_방용환
  // - 기존: 마우스 hover 이벤트를 위한 컴포넌트 내 지역 변수로 선언
  // - 변경: 리랜더링의 영향을 받지 않게 useRef로 관리
  // - 목적: 컴포넌트가 리렌더링되면 hoverCircle이 초기화됨 (기존에 지도에 있던 빨간 점은 setMap(null)을 못 해서 지도에 남음). 그래서 지도 위에 점이 계속 남는 현상 조치

  let hoverCircle = useRef<any>(null) // 마우스 hover 이벤트를 위한 변수

  const page = useRef<number>(1)

  const is_last = useRef<boolean>(false)

  const loading_ref = useRef<boolean>(false)

  const cancel_token = useRef<null | CancelTokenSource>(null)

  const map_filter = useRecoilValue(map_filter_atom)

  const history = useHistory()

  const [loading, setLoading] = useState(true)

  const [data, setData] = useRecoilState(map_side_bar_list_atom)

  const level = map_ref.current.getLevel();

  function onScroll(e: React.UIEvent<HTMLUListElement>) {
    const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget

    if (
      scrollHeight - scrollTop - offsetHeight < 20 &&
      !loading_ref.current &&
      !is_last.current
    ) {
      page.current += 1
      getData()
    }
  }

  function getData() {
    if (cancel_token.current) cancel_token.current.cancel()

    cancel_token.current = axios.CancelToken.source()

    loading_ref.current = true

    setLoading(true)

    // [2025-06-11] by 새날_방용환
    // - 기존: 항상 좌측 리스트 매물 정보 api 요청
    // - 변경: zoom level 6(500m) 이하일 경우에만 api 요청
    // - 목적: zoom level 7(1km) 이상일 때 빠른 속도를 위해 좌측 리스트 접고 매물 정보 미제공
    if(level <= 6) {
      API.estate
        .getMapSideData(map_filter, page.current, cancel_token.current.token)
        .then((res) => {
          apiCallback(res, {
            success: getDataSuccessCallback,
            fail: () => {
              throw new Error()
            },
          })
        })
        .catch(getDataFailCallback)
        .finally(getDataFinallyCallback)
    } else {
      setData([]);
    }
  }

  function getDataSuccessCallback(res: DataModel) {
    setData(page.current === 1 ? res.list : data.concat(res.list))
    is_last.current = res.is_last
  }

  function getDataFailCallback(err: Error | AxiosError) {
    if (!axios.isCancel(err)) {
      setData([])
      is_last.current = true
    }
  }

  function getDataFinallyCallback() {
    setLoading(false)
    loading_ref.current = false
    cancel_token.current = null
  }

  useEffect(() => {
    page.current = 1
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map_filter])

  // [2025-06-11] by 새날_방용환
  // - 기존: 매물 api 요청 미완료 됐을 때 loading 애니메이션 노출
  // - 변경: 매물 api 요청 미완료 및 level 6 이하일 때 내이메이션 노출
  // - 목적: zoom level 7(1km) 이상일 때 빠른 속도를 위해 좌측 리스트 접고 매물 정보 미제공
  if (loading && data.length === 0 && level <= 6) return <LoadingFallback />

  // [2025-06-11] by 새날_방용환
  // - 기존: 매물 api 요청 완료 후 정보 없을 때 <MapSideNodata /> 노출
  // - 변경: 매물 api 요청 완료 후 정보 미보유 및 level 6 이하일 때 <MapSideNodata /> 노출
  // - 변경: zomm level 7 이상일 때 <MapSideLevelOver /> 노출
  // - 목적: zoom level 7(1km) 이상일 때 빠른 속도를 위해 좌측 리스트 접고 매물 정보 미제공
  if (data.length === 0 && level <= 6) {
    return <MapSideNodata />
  } else if(level > 6){
    return <MapSideLevelOver />
  }

  // [2025-06-10] by 새날_방용환
  // - 마우스 hover 시 map-cluster.tsx에서 선언한 data-loc_lat_min, data-loc_lat_max, data-loc_lng_min, data-loc_lng_max를 가지고 그 범위에 포함된 원형 마커의 class 변경
  const highlightMatchingGu = (lat: number, lng: number) => {
    const guElements = document.querySelectorAll('.gu');

    guElements.forEach((el) => {
      const minLat = parseFloat(el.getAttribute('data-loc_min_lat') || '0');
      const maxLat = parseFloat(el.getAttribute('data-loc_max_lat') || '0');
      const minLng = parseFloat(el.getAttribute('data-loc_min_lng') || '0');
      const maxLng = parseFloat(el.getAttribute('data-loc_max_lng') || '0');

      if (
        lat >= minLat &&
        lat <= maxLat &&
        lng >= minLng &&
        lng <= maxLng
      ) {
        el.classList.remove('gu');
        el.classList.add('gu_list_mouse_hover');
      } else {
        el.classList.remove('gu_list_mouse_hover');
        el.classList.add('gu');
      }
    });
  };

  // [2025-06-10] by 새날_방용환
  // - 리스트에서 마우스 leave 시 마커의 class 변경
  const clearGuHoverHighlight = () => {
    document.querySelectorAll('.gu_list_mouse_hover').forEach((el) => {
      el.classList.remove('gu_list_mouse_hover');
      el.classList.add('gu');
    });
  };

  return (
    <ul className={fold ? 'hide' : undefined} onScroll={onScroll}>
      {data.map((item) => {

        const modifiedPriceText =
        item.price_text.includes('임대') && item.price_text.includes('월')
          ? item.price_text.replace(/월\s*/g, '')
          : item.price_text;

        return (
          <li
            key={item.idx}
            data-list-lat={item.lat}
            data-list-lng={item.lng}
            onClick={() =>
              history.push(
                `/sale-detail-type${getEstateDetailPath(
                  item.deal_kind_text,
                  item.idx,
                  item.status === 'COMPLETE'
                )}`
              )
            }
            onMouseEnter={() => {
              highlightMatchingGu(item.lat, item.lng);
            }}
            onMouseLeave={() => {
              clearGuHoverHighlight();
            }}
          >
            {item.thumbnail_path ? (
              <div className='img_holder'>
                <img
                  src={item.thumbnail_path}
                  alt=''
                  onContextMenu={(e) => {
                    e.preventDefault()
                  }}
                />
                <div
                  className={`tag ${
                    item.status === 'COMPLETE'
                      ? 'bg-complate'
                      : item.type_text === '공장'
                      ? 'bg-factory'
                      : item.type_text === '창고'
                      ? 'bg-store'
                      : item.type_text === '토지'
                      ? 'bg-ground'
                      : item.type_text === '지식' ||
                        item.type_text === '지식산업센터'
                      ? 'bg-center'
                      : item.type_text === '산업' ||
                        item.type_text === '산업단지'
                      ? 'bg-industry'
                      : 'bg-logistics'
                  }`}
                >
                  {item.type_text}
                </div>
                
                {item.map_yn === 0 && (
                  <div className="ad_icon">AD</div>
                )}
              </div>
            ) : (
              <></>
            )}
            <div className={`list_desc ${item.thumbnail_path ? '' : 'full'}`}>
              <div className='desc_top'>
                {item.thumbnail_path ? (
                  <></>
                ) : (
                  <>
                    <div
                      className={`tag ${
                        item.status === 'COMPLETE'
                          ? 'bg-complate'
                          : item.type_text === '공장'
                          ? 'bg-factory'
                          : item.type_text === '창고'
                          ? 'bg-store'
                          : item.type_text === '토지'
                          ? 'bg-ground'
                          : item.type_text === '지식' ||
                            item.type_text === '지식산업센터'
                          ? 'bg-center'
                          : item.type_text === '산업' ||
                            item.type_text === '산업단지'
                          ? 'bg-industry'
                          : 'bg-logistics'
                      }`}
                    >
                      {item.type_text}
                    </div>
                    {item.recommend_map_yn === 'Y' && (
                      <div className="ad_icon">AD</div>
                    )}
                  </>
                )}
                {/* {item.price_text ? (
                  <div className='desc_middle'>
                    <p className='fs-xs ellipsis price_text'>{item.price_text}</p>
                  </div>
                ) : (
                  <></>
                )} */}
                {(item.type_text === '산업단지' || modifiedPriceText) && (
                  <div className='desc_middle'>
                    <p className='fs-xs ellipsis price_text'>
                      {item.type_text === '산업단지' ? item.title : modifiedPriceText}
                    </p>
                  </div>
                )}
              </div>
              
              <div className='desc_middle'>
                <h3 className='title ellipsis'>{item.address_text}</h3>
              </div>
              <div className='desc_middle'>
                {item.price_per_area_yn === 'Y' &&
                  item.deal_kind_text === '분양' && (
                    // <p className='c-price fs-s fw500'>{item.price_text}</p>
                    <p className='c-price fs-s fw500'>{modifiedPriceText}</p>
                  )}
                  {item.type === "KC" ? (
                    <p className='c-ink2 fs-s fw500'>{item.supply_area_text}</p>
                  ) : (
                    <p className='c-ink2 fs-s fw500'>{item.total_plat_area_text}</p>
                  )}
                  
              </div>
              {item.tags_text ? (
                <div className='desc_bottom'>
                  <p className='c-hash fs-xs ellipsis'>{item.tags_text}</p>
                </div>
              ) : (
                <></>
              )}
            </div>
          </li>
        )
      })}
      {loading && data.length > 0 && (
        <li>
          <LoadingFallback className='mg-10' />
        </li>
      )}
    </ul>
  )
}

export default MapSideBarList
